import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import pica from 'pica';
import styles from './css/PixelConverter.module.css';

const clampDimension = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return 64;
  return Math.min(4096, Math.max(1, parsed));
};

const MIME_TYPE_BY_FORMAT = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

const fitImageRect = (sourceWidth, sourceHeight, targetWidth, targetHeight, mode) => {
  if (mode === 'stretch') {
    return {
      sx: 0,
      sy: 0,
      sw: sourceWidth,
      sh: sourceHeight,
      dx: 0,
      dy: 0,
      dw: targetWidth,
      dh: targetHeight,
    };
  }

  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = targetWidth / targetHeight;

  if (mode === 'cover') {
    let sx = 0;
    let sy = 0;
    let sw = sourceWidth;
    let sh = sourceHeight;

    if (sourceAspect > targetAspect) {
      sw = sourceHeight * targetAspect;
      sx = (sourceWidth - sw) / 2;
    } else {
      sh = sourceWidth / targetAspect;
      sy = (sourceHeight - sh) / 2;
    }

    return {
      sx,
      sy,
      sw,
      sh,
      dx: 0,
      dy: 0,
      dw: targetWidth,
      dh: targetHeight,
    };
  }

  let dw = targetWidth;
  let dh = targetHeight;

  if (sourceAspect > targetAspect) {
    dh = Math.round(targetWidth / sourceAspect);
  } else {
    dw = Math.round(targetHeight * sourceAspect);
  }

  return {
    sx: 0,
    sy: 0,
    sw: sourceWidth,
    sh: sourceHeight,
    dx: Math.round((targetWidth - dw) / 2),
    dy: Math.round((targetHeight - dh) / 2),
    dw,
    dh,
  };
};

const PixelConverter = () => {
  const [width, setWidth] = useState(64);
  const [height, setHeight] = useState(64);
  const [sourceFile, setSourceFile] = useState(null);
  const [baseName, setBaseName] = useState('converted-image');
  const [error, setError] = useState('');
  const [fitMode, setFitMode] = useState('contain');
  const [resizeMode, setResizeMode] = useState('high-quality');
  const [format, setFormat] = useState('png');
  const [quality, setQuality] = useState(92);
  const [processing, setProcessing] = useState(false);
  const [hasPreview, setHasPreview] = useState(false);

  const outputCanvasRef = useRef(null);
  const picaRef = useRef(null);

  if (!picaRef.current) {
    picaRef.current = pica({ features: ['js', 'wasm', 'ww'] });
  }

  const outputName = useMemo(
    () => `${baseName}-${width}x${height}.${format === 'jpeg' ? 'jpg' : format}`,
    [baseName, width, height, format]
  );

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      setError('Please upload a valid image file.');
      return;
    }

    const file = acceptedFiles[0];
    if (!file) return;

    setSourceFile(file);
    const fileNameWithoutExt = file.name.includes('.')
      ? file.name.slice(0, file.name.lastIndexOf('.'))
      : file.name;
    setBaseName(fileNameWithoutExt || 'converted-image');
    setError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'image/*': [],
    },
    maxFiles: 1,
  });

  useEffect(() => {
    if (!sourceFile) return;

    let cancelled = false;
    const image = new Image();
    const fileUrl = URL.createObjectURL(sourceFile);

    setHasPreview(false);
    setProcessing(true);

    const processImage = async () => {
      setProcessing(true);
      if (cancelled) return;

      const canvas = outputCanvasRef.current;
      if (!canvas) return;

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (!context) {
        setError('Unable to render image on this browser.');
        setProcessing(false);
        return;
      }

      context.clearRect(0, 0, width, height);

      const rect = fitImageRect(image.width, image.height, width, height, fitMode);
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = Math.max(1, Math.round(rect.sw));
      srcCanvas.height = Math.max(1, Math.round(rect.sh));

      const srcContext = srcCanvas.getContext('2d');
      if (!srcContext) {
        setError('Unable to process source image.');
        setProcessing(false);
        return;
      }

      srcContext.drawImage(
        image,
        rect.sx,
        rect.sy,
        rect.sw,
        rect.sh,
        0,
        0,
        srcCanvas.width,
        srcCanvas.height
      );

      const targetCanvas = document.createElement('canvas');
      targetCanvas.width = Math.max(1, Math.round(rect.dw));
      targetCanvas.height = Math.max(1, Math.round(rect.dh));

      if (resizeMode === 'high-quality') {
        await picaRef.current.resize(srcCanvas, targetCanvas, {
          alpha: true,
          unsharpAmount: 80,
          unsharpRadius: 0.6,
          unsharpThreshold: 2,
        });
      } else {
        const pixelCtx = targetCanvas.getContext('2d');
        if (!pixelCtx) {
          setError('Unable to process target image.');
          setProcessing(false);
          return;
        }

        pixelCtx.imageSmoothingEnabled = false;
        pixelCtx.drawImage(srcCanvas, 0, 0, targetCanvas.width, targetCanvas.height);
      }

      if (cancelled) return;

      context.imageSmoothingEnabled = false;
      context.drawImage(
        targetCanvas,
        0,
        0,
        targetCanvas.width,
        targetCanvas.height,
        rect.dx,
        rect.dy,
        rect.dw,
        rect.dh
      );

      setError('');
      setHasPreview(true);
      setProcessing(false);
    };

    image.onload = () => {
      processImage().catch(() => {
        if (!cancelled) {
          setError('Could not process this image.');
          setHasPreview(false);
          setProcessing(false);
        }
      });
    };

    image.onerror = () => {
      if (!cancelled) {
        setError('Could not load this file as an image.');
        setHasPreview(false);
        setProcessing(false);
      }
    };

    image.src = fileUrl;

    return () => {
      cancelled = true;
      URL.revokeObjectURL(fileUrl);
    };
  }, [sourceFile, width, height, fitMode, resizeMode]);

  const handleDownload = () => {
    const canvas = outputCanvasRef.current;
    if (!canvas || !sourceFile || !hasPreview) return;

    const mimeType = MIME_TYPE_BY_FORMAT[format] || MIME_TYPE_BY_FORMAT.png;
    const qualityValue = format === 'png' ? undefined : quality / 100;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = outputName;
      link.click();
      URL.revokeObjectURL(url);
    }, mimeType, qualityValue);
  };

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <h1>64x64 Pixel Graphic Converter</h1>
        <p>
          Upload any image and convert it to a pixel graphic. Default output is 64x64,
          and you can set custom dimensions, fit mode, and output format.
        </p>

        <div className={styles.controls}>
          <div
            {...getRootProps()}
            className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ''}`}
          >
            <input {...getInputProps()} />
            <p>{isDragActive ? 'Drop your image here' : 'Drag and drop an image, or click to upload'}</p>
          </div>

          <label className={styles.field}>
            <span>Width (px)</span>
            <input
              type="number"
              min="1"
              max="4096"
              value={width}
              onChange={(event) => setWidth(clampDimension(event.target.value))}
            />
          </label>

          <label className={styles.field}>
            <span>Height (px)</span>
            <input
              type="number"
              min="1"
              max="4096"
              value={height}
              onChange={(event) => setHeight(clampDimension(event.target.value))}
            />
          </label>

          <label className={styles.field}>
            <span>Fit Mode</span>
            <select value={fitMode} onChange={(event) => setFitMode(event.target.value)}>
              <option value="contain">Contain</option>
              <option value="cover">Cover</option>
              <option value="stretch">Stretch</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Resize Engine</span>
            <select value={resizeMode} onChange={(event) => setResizeMode(event.target.value)}>
              <option value="high-quality">High Quality</option>
              <option value="pixelated">Pixel Crisp</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Output Format</span>
            <select value={format} onChange={(event) => setFormat(event.target.value)}>
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
              <option value="webp">WebP</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Compression Quality: {quality}%</span>
            <input
              type="range"
              min="40"
              max="100"
              step="1"
              value={quality}
              onChange={(event) => setQuality(Number.parseInt(event.target.value, 10) || 92)}
              disabled={format === 'png'}
            />
          </label>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {processing && <p className={styles.status}>Processing image...</p>}

        <div className={styles.previewWrap}>
          <canvas ref={outputCanvasRef} className={styles.previewCanvas} />
          {!sourceFile && <p className={styles.placeholder}>Upload an image to preview the converted result.</p>}
        </div>

        <button
          type="button"
          className={styles.downloadButton}
          onClick={handleDownload}
          disabled={!sourceFile || processing || !hasPreview}
        >
          Download {format.toUpperCase()}
        </button>
      </section>
    </main>
  );
};

export default PixelConverter;
